import { useCallback, useMemo } from 'react';
import { CompanyProfileBenefit } from '~api';
import { useFormatMessage } from './use-format-message';

export const useFormatBenefit = () => {
  const formatMessage = useFormatMessage();
  const mapBenefitToLabel = useMemo<Record<CompanyProfileBenefit, string | React.ReactNodeArray>>(
    () => ({
      [CompanyProfileBenefit.ADDITIONAL_SOCIAL_BENEFITS]: formatMessage({
        id: 'comp.benefit.additional_social_benefits',
        defaultMessage: 'Additional social benefits',
      }),
      [CompanyProfileBenefit.CHILDCARE_SUPPORT]: formatMessage({
        id: 'comp.benefit.childcare_support',
        defaultMessage: 'Childcare support',
      }),
      [CompanyProfileBenefit.COMPANY_CAR]: formatMessage({
        id: 'comp.benefit.company_car',
        defaultMessage: 'Company car',
      }),
      [CompanyProfileBenefit.COMPANY_PHONE]: formatMessage({
        id: 'comp.benefit.company_phone',
        defaultMessage: 'Company phone',
      }),
      [CompanyProfileBenefit.DENTAL_CARE]: formatMessage({
        id: 'comp.benefit.dental_care',
        defaultMessage: 'Dental care',
      }),
      [CompanyProfileBenefit.DISCOUNTS_FROM_EXTERNAL_PARTNERS]: formatMessage({
        id: 'comp.benefit.discounts_from_external_partners',
        defaultMessage: 'Discounts from external partners',
      }),
      [CompanyProfileBenefit.DRINKS_AT_THE_OFFICE]: formatMessage({
        id: 'comp.benefit.drinks_at_the_office',
        defaultMessage: 'Drinks at the office',
      }),
      [CompanyProfileBenefit.EDUCATION_BUDGET]: formatMessage({
        id: 'comp.benefit.education_budget',
        defaultMessage: 'Education budget',
      }),
      [CompanyProfileBenefit.EMPLOYEE_DISCOUNTS]: formatMessage({
        id: 'comp.benefit.employee_discounts',
        defaultMessage: 'Employee discounts',
      }),
      [CompanyProfileBenefit.EMPLOYEE_REFERRAL_PROGRAM]: formatMessage({
        id: 'comp.benefit.employee_referral_program',
        defaultMessage: 'Employee referral program',
      }),
      [CompanyProfileBenefit.EXTRA_DAYS_OFF]: formatMessage({
        id: 'comp.benefit.extra_days_off',
        defaultMessage: 'Extra days off',
      }),
      [CompanyProfileBenefit.FLEXIBLE_WORKING_HOURS]: formatMessage({
        id: 'comp.benefit.flexible_working_hours',
        defaultMessage: 'Flexible working hours',
      }),
      [CompanyProfileBenefit.FREE_MEALS]: formatMessage({
        id: 'comp.benefit.free_meals.',
        defaultMessage: 'Free meals',
      }),
      [CompanyProfileBenefit.FRUITS_AT_THE_OFFICE]: formatMessage({
        id: 'comp.benefit.fruits_at_the_office',
        defaultMessage: 'Fruits at the office',
      }),
      [CompanyProfileBenefit.IT_EQUIPMENT_OF_CHOICE]: formatMessage({
        id: 'comp.benefit.it_equipment_of_choice',
        defaultMessage: 'IT equipment of choice',
      }),
      [CompanyProfileBenefit.LANGUAGE_COURSES]: formatMessage({
        id: 'comp.benefit.language_courses',
        defaultMessage: 'Language courses',
      }),
      [CompanyProfileBenefit.LIFE_INSURANCE]: formatMessage({
        id: 'comp.benefit.life_insurance',
        defaultMessage: 'Life insurance',
      }),
      [CompanyProfileBenefit.PARTIES_AND_EVENTS]: formatMessage({
        id: 'comp.benefit.parties_and_events',
        defaultMessage: 'Parties & events',
      }),
      [CompanyProfileBenefit.PRIVATE_MEDICAL_INSURANCE]: formatMessage({
        id: 'comp.benefit.private_medical_insurance',
        defaultMessage: 'Private medical insurance',
      }),
      [CompanyProfileBenefit.RELOCATION_ASSISTANCE]: formatMessage({
        id: 'comp.benefit.relocation_assistance',
        defaultMessage: 'Relocation assistance',
      }),
      [CompanyProfileBenefit.REMOTE_WORK]: formatMessage({
        id: 'comp.benefit.remote_work',
        defaultMessage: 'Possibility of remote work',
      }),
      [CompanyProfileBenefit.SUBSIDIED_MEALS]: formatMessage({
        id: 'comp.benefit.subsidied_meals',
        defaultMessage: 'Subsidized meals',
      }),
      [CompanyProfileBenefit.SUBSIDIED_SPORTS]: formatMessage({
        id: 'comp.benefit.subsidied_sports',
        defaultMessage: 'Subsidy for sports activities',
      }),
      [CompanyProfileBenefit.SUBSIDIED_VACATIONS]: formatMessage({
        id: 'comp.benefit.subsidied_vacations',
        defaultMessage: 'Subsidised vacations',
      }),
    }),
    [formatMessage],
  );

  return useCallback(
    (benefit: CompanyProfileBenefit) => mapBenefitToLabel[benefit],
    [mapBenefitToLabel],
  );
};
